import Vue from "vue";
import { api } from "@/services";
import { getTempId, isEqual } from "@/helpers";
import { isTempId } from "@/helpers";

export const ContactPersonV2ItemStatus = {
  NONE: "",
  CREATED: "created",
  UPDATED: "updated",
  REMOVED: "removed",
};

export const ContactPersonV2StateStatus = {
  NONE: "",
  LOADING: "loading",
  ERROR: "error",
};

export const contactPersonV2 = {
  namespaced: true,
  state: {
    items: [],
    item: {},
    removedItems: [],
    status: ContactPersonV2StateStatus.NONE,
    error: {},
    totalItems: 0,
  },
  getters: {
    createdContactPersons: (state) => {
      return state.items.filter((item) => item.status === ContactPersonV2ItemStatus.CREATED);
    },
    updatedContactPersons: (state) => {
      return state.items.filter((item) => item.status === ContactPersonV2ItemStatus.UPDATED);
    },
    removedContactPersons: (state) => {
      return state.removedItems;
    },
  },
  actions: {
    list({ commit }, { page, itemsPerPage, sort, search }) {
      commit("SET_STATUS_LOADING");

      return api.contactPerson
        .list(page, itemsPerPage, sort, search)
        .then((response) => {
          commit("SET_ITEMS", response.data.results);
          commit("UNSET_STATUS");
        })
        .catch((error) => commit("SET_STATUS_ERROR", error));
    },
    listByIds({ commit }, { ids }) {
      commit("SET_STATUS_LOADING");
      commit("UNSET_ITEMS");

      let items = [];
      ids.forEach((id) => {
        api.contactPerson
          .detail(id)
          .then((response) => items.push(response.data))
          .catch((error) => commit("SET_STATUS_ERROR", error));
      });

      commit("SET_ITEMS", items);
      commit("UNSET_STATUS");
    },
    detail({ commit }, { id }) {
      commit("SET_STATUS_LOADING");
      commit("UNSET_ITEM");
      return api.contactPerson
        .detail(id)
        .then((response) => {
          commit("SET_ITEM", response.data);
          commit("UNSET_STATUS");
        })
        .catch((error) => commit("SET_STATUS_ERROR", error));
    },
    createRemote({ commit }, { object }) {
      commit("SET_STATUS_LOADING");

      return api.contactPerson
        .create(object)
        .then((item) => {
          commit("ADD_ITEM", item.data);
          commit("UNSET_STATUS");
        })
        .catch((error) => commit("SET_STATUS_ERROR", error));
    },
    createLocal({ commit }, { object }) {
      commit("ADD_ITEM", object);
    },
    updateRemote({ commit }, { object }) {
      commit("SET_STATUS_LOADING");

      return api.contactPerson
        .update(object.id, object)
        .then((item) => {
          commit("UPDATE_ITEM", item);
          commit("UNSET_STATUS");
        })
        .catch((error) => commit("SET_STATUS_ERROR", error));
    },
    updateLocal({ commit }, { object }) {
      commit("SET_STATUS_LOADING");

      commit("UPDATE_ITEM", object);

      commit("UNSET_STATUS");
    },
    removeRemote({ commit }, { object }) {
      commit("SET_STATUS_LOADING");

      return api.contactPerson
        .remove(object.id)
        .then(() => {
          commit("REMOVE_ITEM", object);
          commit("UNSET_STATUS");
        })
        .catch((error) => commit("SET_STATUS_ERROR", error));
    },
    removeLocal({ commit }, { object }) {
      commit("SET_STATUS_LOADING");

      commit("REMOVE_ITEM", object);

      commit("UNSET_STATUS");
    },
    addEmptyItem({ commit }) {
      commit("UNSET_STATUS");

      let emptyItem = {
        id: getTempId("contactPerson"),
        name: "",
        email: "",
        phone: "",
        comment: "",
        language: "",
        default: false,
      };

      commit("ADD_ITEM", emptyItem);
    },
    unsetItems({ commit }) {
      commit("UNSET_ITEMS");
    },
    unsetItem({ commit }) {
      commit("UNSET_ITEM");
    },
    unsetRemovedItems({ commit }) {
      commit("UNSET_REMOVED_ITEMS");
    },
    unsetAllItems({ commit }) {
      commit("UNSET_ALL_ITEMS");
    },
  },
  mutations: {
    UNSET_ITEMS(state) {
      state.items = [];
    },
    UNSET_ITEM(state) {
      state.item = {};
    },
    UNSET_REMOVED_ITEMS(state) {
      state.removedItems = [];
    },
    UNSET_ALL_ITEMS(state) {
      state.items = [];
      state.item = {};
      state.removedItems = [];
    },
    SET_STATUS_ERROR(state, error) {
      state.error = error;
      state.status = ContactPersonV2StateStatus.ERROR;
    },
    SET_STATUS_LOADING(state) {
      state.error = {};
      state.status = ContactPersonV2StateStatus.LOADING;
    },
    UNSET_STATUS(state) {
      state.error = {};
      state.status = ContactPersonV2StateStatus.NONE;
    },
    ADD_ITEM(state, item) {
      item.status = ContactPersonV2ItemStatus.CREATED;
      state.items.push(item);
      state.totalItems = state.items.length;
    },
    SET_ITEM(state, item) {
      state.item = item;
    },
    SET_ITEMS(state, items) {
      state.items = items;
      state.totalItems = state.items.length;
    },
    UPDATE_ITEM(state, item) {
      if (!state.items) {
        return;
      }
      let newItem = { ...item };

      if (newItem.default) {
        // * Reset all items to default - false;
        state.items.forEach((storeItem) => {
          storeItem.default = false;
        });
      }

      let index = state.items.findIndex((stateItem) => isEqual(stateItem.id, item.id));
      if (index > -1) {
        if (!isTempId(item.id)) {
          newItem.status = ContactPersonV2ItemStatus.UPDATED;
        } else {
          newItem.status = state.items[index].status;
        }

        Vue.set(state.items, index, newItem);
      }
    },
    REMOVE_ITEM(state, item) {
      if (!state.items) {
        return;
      }
      let newStateItems = [];
      for (let index = 0; index < state.items.length; ++index) {
        let stateItem = state.items[index];
        if (isEqual(stateItem.id, item.id)) {
          if (!isTempId(stateItem.id)) {
            stateItem.status = ContactPersonV2ItemStatus.REMOVED;
            state.removedItems.push(stateItem);
          }
        } else {
          newStateItems.push(stateItem);
        }
      }
      state.items = newStateItems;
      state.totalItems = state.items.length;
    },
  },
};

export default {
  contactPersonV2,
  ContactPersonV2StateStatus,
  ContactPersonV2ItemStatus,
};
